import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, Signal, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DsButtonColor,
  DsButtonVariants,
  DsLinkColor,
  DsModalService,
  DsModalSlideOutAlignment,
  DsModalVariant,
  DsTooltipPosition,
  Icons,
  IconStyles,
  IDsBreadcrumbItem,
  DsButtonBorderColors,
} from '@levelaccess/design-system';

import { Api } from '../../../../../shared/constants/api';
import { RequiredSecurities } from '../../../../../shared/constants/required-securities';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { AclSecurityAdapter } from '../../../services/acl.service';
import { AppConfigService } from '../../../services/app-config.service';
import { ExternalLinkService } from '../../../services/external-link.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { INavigationContextItem, INavMenuItem, NavigationItem } from '../../../services/navigation/nav-structure';
import { NavService } from '../../../services/navigation/nav.service';
import { UserAclService } from '../../../services/user-acl.service';
import { LauncherNavService } from '../../../services/navigation/launcher-nav.service';
import { AiService } from '../../../services/ai.service';
import { VerticalNavigationStateService } from '../../vertical-navigation/vertical-navigation-state.service';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';

@Component({
  selector: 'compound-top-navbar',
  templateUrl: './compound-top-navbar.component.html',
  styleUrls: ['./compound-top-navbar.component.scss'],
  standalone: false,
})
export class CompoundTopNavbarComponent implements OnInit, AfterViewInit {
  @ViewChild('mobileNavModal') public mobileNavModal: TemplateRef<any>;
  @ViewChild('launcherModal') public launcherModal: TemplateRef<any>;

  @Input() public showMobileNavVersion: boolean;

  public Icons: typeof Icons;
  public IconStyles: typeof IconStyles;
  public DsLinkColor: typeof DsLinkColor;
  public DsButtonVariants: typeof DsButtonVariants;
  public DsTooltipPosition: typeof DsTooltipPosition;
  public DsButtonColor: typeof DsButtonColor;
  public DsButtonBorderColors: typeof DsButtonBorderColors;
  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public readonly RequiredSecurities: typeof RequiredSecurities = RequiredSecurities;

  public hasAccessToNotifications$: Observable<boolean>;
  public hasAccessToAdminOrganizations$: Observable<boolean>;
  public hasAccessToNewDashboard$: Observable<boolean>;
  public hasAccessToPortfolio$: Observable<boolean>;
  public logoUrl$: Observable<string>;

  public academyNavigationItem$: Observable<INavMenuItem>;
  public oldHelpNavigationItem$: Observable<INavMenuItem>;
  public helpNavigationItem$: Observable<INavMenuItem>;
  public serviceRequestNavigationItem$: Observable<INavMenuItem>;
  public navSupportPortalUxEnabled$: Observable<boolean>;
  protected breadcrumbs$: Observable<IDsBreadcrumbItem[]>;
  protected showAiChatButton$: Observable<boolean>;
  protected showAiChatButtonMobile$: Observable<boolean>;
  protected hasAccessToLauncher: boolean;
  protected isVerticalNavigationEnabled: Signal<boolean>;
  protected navSwitchOptionVisible: Signal<boolean>;
  protected breadcrumbsMaxCharacters$: Observable<number>;
  protected currentContextItem: Signal<INavigationContextItem>;

  constructor(
    protected externalLinkService: ExternalLinkService,
    private navService: NavService,
    private userAclService: UserAclService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigService: AppConfigService,
    private dsModalService: DsModalService,
    private featureFlagService: FeatureFlagService,
    private launcherNavService: LauncherNavService,
    private aiService: AiService,
    private verticalNavigationStateService: VerticalNavigationStateService,
  ) {
    this.Icons = Icons;
    this.IconStyles = IconStyles;
    this.DsLinkColor = DsLinkColor;
    this.DsButtonVariants = DsButtonVariants;
    this.DsTooltipPosition = DsTooltipPosition;
    this.DsButtonColor = DsButtonColor;
    this.DsButtonBorderColors = DsButtonBorderColors;
    this.currentContextItem = toSignal(this.navService.getCurrentContextItem$());
    this.isVerticalNavigationEnabled = toSignal(this.navService.isVerticalNavigationEnabled$);

    this.navSwitchOptionVisible = toSignal(
      combineLatest([
        this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigation, false),
        this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigationOptin, false),
        this.featureFlagService.variation$(FeatureFlagCollection.moveNavToggle, false),
      ]).pipe(
        map(
          ([verticalNavigationFullyEnabled, verticalNavigationOptinEnabled, moveNavToggleEnabled]: [
            boolean,
            boolean,
            boolean,
          ]) => {
            return !verticalNavigationFullyEnabled && verticalNavigationOptinEnabled && !moveNavToggleEnabled;
          },
        ),
      ),
      { initialValue: false },
    );

    this.hasAccessToNotifications$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useFunctionalActions(RequiredSecurities.Notifications_Read.functionalActions).check(),
        ),
      );

    this.hasAccessToAdminOrganizations$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useFunctionalActions(RequiredSecurities.Tenants_Read.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .useRequiredSecurity(RequiredSecurities.Tenants_Read, true)
            .check(),
        ),
      );

    this.hasAccessToNewDashboard$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useFunctionalActions(RequiredSecurities.GR_Governance_Read.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .useRequiredSecurity(RequiredSecurities.GR_Governance_Read, false)
            .check(),
        ),
      );

    this.hasAccessToPortfolio$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useFunctionalActions(RequiredSecurities.Workspaces_Read_Digital_Properties_Read.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .useRequiredSecurity(RequiredSecurities.Workspaces_Read_Digital_Properties_Read, false)
            .check(),
        ),
      );

    this.logoUrl$ = combineLatest([
      this.hasAccessToAdminOrganizations$,
      this.hasAccessToNewDashboard$,
      this.hasAccessToPortfolio$,
    ]).pipe(
      map(
        ([hasAccessToAdminOrganizations, hasAccessToNewDashboard, hasAccessToPortfolio]: [boolean, boolean, boolean]): string => {
          if (!this.appConfigService.isTenantEnabled()) {
            if (hasAccessToAdminOrganizations) {
              return `/${Api.admin}/${Api.organizations}`;
            }
            return `/${Api.settings}/${Api.profile}`;
          }

          if (hasAccessToNewDashboard) {
            return `/${Api.overview}`;
          }

          if (hasAccessToPortfolio) {
            return `/${Api.portfolio}`;
          }

          return `/${Api.dashboard}`;
        },
      ),
    );

    this.showAiChatButton$ = combineLatest([
      this.aiService.aiEnabledForTenant$,
      this.featureFlagService.variation$(FeatureFlagCollection.aiChat, false),
      this.navService.isVerticalNavigationEnabled$,
    ]).pipe(
      map(([aiEnabledForTenant, aiChatFlagEnabled, isVerticalNavigationEnabled]: [boolean, boolean, boolean]): boolean => {
        return aiEnabledForTenant && aiChatFlagEnabled && isVerticalNavigationEnabled;
      }),
    );
  }

  public openMobileMenu(): void {
    this.dsModalService.open(this.mobileNavModal, DsModalVariant.fullScreen);
  }

  public openLauncher(): void {
    this.dsModalService.open(this.launcherModal, DsModalVariant.slideOut, DsModalSlideOutAlignment.start);
  }

  public ngOnInit(): void {
    this.showAiChatButtonMobile$ = combineLatest([
      this.aiService.aiEnabledForTenant$,
      this.featureFlagService.variation$(FeatureFlagCollection.aiChat, false),
      this.navService.isVerticalNavigationEnabled$,
    ]).pipe(
      map(([aiEnabledForTenant, aiChatFlagEnabled, isVerticalNavigationEnabled]: [boolean, boolean, boolean]): boolean => {
        return aiEnabledForTenant && aiChatFlagEnabled && !isVerticalNavigationEnabled;
      }),
    );

    this.academyNavigationItem$ = this.navService
      .getTopLevelMenuItems$([NavigationItem.academy])
      .pipe(map((menuItems: INavMenuItem[]): INavMenuItem => menuItems?.filter((item: INavMenuItem) => item?.isAvailable)?.[0]));
    this.oldHelpNavigationItem$ = this.navService
      .getTopLevelMenuItems$([NavigationItem.old_help])
      .pipe(map((menuItems: INavMenuItem[]): INavMenuItem => menuItems?.[0]));
    this.helpNavigationItem$ = this.navService
      .getTopLevelMenuItems$([NavigationItem.help])
      .pipe(map((menuItems: INavMenuItem[]): INavMenuItem => menuItems?.[0]));
    this.serviceRequestNavigationItem$ = this.navService
      .getTopLevelMenuItems$([NavigationItem.service_request])
      .pipe(map((menuItems: INavMenuItem[]): INavMenuItem => menuItems?.[0]));

    this.breadcrumbs$ = this.navService.buildBreadcrumbs$();

    this.breadcrumbsMaxCharacters$ = this.breadcrumbs$.pipe(
      map((breadcrumbs: IDsBreadcrumbItem[]): number => {
        if (!breadcrumbs || breadcrumbs.length === 0) {
          return 20;
        }

        const breadcrumbCount = breadcrumbs.length;
        if (breadcrumbCount === 1) {
          return 40;
        }
        if (breadcrumbCount === 2) {
          return 25;
        }
        return 20;
      }),
    );

    this.navSupportPortalUxEnabled$ = this.featureFlagService
      .variation$(FeatureFlagCollection.navSupportPortalUx, false, true)
      .pipe(map(Boolean));

    this.hasAccessToLauncher = this.launcherNavService.hasAccessToLauncher;
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  public openVerticalNavigationMenu(): void {
    this.verticalNavigationStateService.openVerticalNavigationModal();
  }

  public navMenuItemLink(menuItem: INavMenuItem): string {
    if (SharedCommonUtility.notNullish(menuItem.externalLinkFn)) {
      return menuItem.externalLinkFn(this.externalLinkService);
    }
    return menuItem.routerLink;
  }
}
